// src/pages/Projects/ProjectDetails.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import MuiSidebar from "../../components/MuiSidebar";
import AnimatedBackground from "../../components/AnimatedBackground";

import {
  Typography,
  Box,
  Select,
  MenuItem,
  TextField,
  Fab,
  Button,
  Chip,
} from "@mui/material";
import { FiDownload, FiFile, FiMap } from "react-icons/fi";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { styled, useTheme, alpha } from "@mui/material/styles";
import {
  LocationCity as LocationCityIcon,
  Business as BusinessIcon,
  HomeWork as HomeWorkIcon,
  House as HouseIcon,
  Tag as TagIcon,
  SquareFoot as SquareFootIcon,
  Grass as GrassIcon,
  KingBed as KingBedIcon,
  Stairs as StairsIcon,
} from "@mui/icons-material";

import {
  DetailsContainer,
  DetailsCard,
  DetailsGrid,
} from "./ProjectDetailsStyles";

import ProjectDetailsSkeleton from "./ProjectDetailsSkeleton";
import axios from "axios";
import SessionExpiredPopup from "../../components/SessionExpiredPopup/SessionExpiredPopup";
import useSessionExpired from "../../hooks/useSessionExpired";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

// Chart.js (Bar)
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/* -------------------- Utilities -------------------- */

const parseApiDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr !== "string") return new Date(dateStr);
  // Support both "MM/DD/YYYY" and "YYYY-MM-DD"
  if (dateStr.includes("-")) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }
  const [month, day, year] = dateStr.split("/").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const formatMonthYear = (dateStr) => {
  if (!dateStr) return "";
  const d = parseApiDate(dateStr);
  if (!d || isNaN(d)) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${mm}/${d.getFullYear()}`;
};

const filterLastNMonths = (data, n) => {
  if (!data || !data.labels || data.labels.length === 0) return data;
  const dates = data.labels.map(parseApiDate);
  const lastDate = dates[dates.length - 1];
  const cutoffDate = new Date(lastDate);
  cutoffDate.setMonth(cutoffDate.getMonth() - n);

  const filteredIndices = dates.reduce((acc, date, index) => {
    if (date >= cutoffDate) acc.push(index);
    return acc;
  }, []);

  return {
    labels: filteredIndices.map((i) => data.labels[i]),
    totalProfit: filteredIndices.map((i) => data.totalProfit[i]),
    operationProfit: filteredIndices.map((i) => data.operationProfit[i]),
    assetProfit: filteredIndices.map((i) => data.assetProfit[i]),
  };
};

const filterByDateRange = (data, start, end) => {
  if (!data || !data.labels || !start || !end) return data;
  const startDate = parseApiDate(start);
  const endDate = parseApiDate(end);
  const dates = data.labels.map(parseApiDate);

  const filteredIndices = dates.reduce((acc, date, index) => {
    if (date >= startDate && date <= endDate) acc.push(index);
    return acc;
  }, []);

  return {
    labels: filteredIndices.map((i) => data.labels[i]),
    totalProfit: filteredIndices.map((i) => data.totalProfit[i]),
    operationProfit: filteredIndices.map((i) => data.operationProfit[i]),
    assetProfit: filteredIndices.map((i) => data.assetProfit[i]),
  };
};

/**
 * Improved coordinate extractor.
 * Handles:
 *  - wikimapia (e.g. ...#lang=en&lat=31.082342&lon=28.409185...)
 *  - google maps "@lat,lon"
 *  - /maps/search/lat,lon
 *  - q=lat,lon
 *  - plain "lat,lon"
 *
 * Returns { lat, lon } or null.
 */
const extractCoordinatesFromGoogleMaps = (url) => {
  try {
    if (!url) return null;
    // decode and normalize escaped slashes
    let s = String(url);
    try {
      s = decodeURIComponent(s);
    } catch (e) {
      /* ignore */
    }
    s = s.replace(/\\\//g, "/");

    // wikimapia style: lat=...&lon=...
    let m = s.match(/[?&#]lat=(-?\d+(\.\d+)?)&lon=(-?\d+(\.\d+)?)/i);
    if (m) return { lat: parseFloat(m[1]), lon: parseFloat(m[3]) };

    // Sometimes with param order lon then lat (rare) - check both
    m = s.match(/[?&#]lon=(-?\d+(\.\d+)?)&lat=(-?\d+(\.\d+)?)/i);
    if (m) return { lat: parseFloat(m[3]), lon: parseFloat(m[1]) };

    // /maps/search/lat,lon
    m = s.match(/\/maps\/search\/(-?\d+(\.\d+)?),\+?(-?\d+(\.\d+)?)/i);
    if (m) return { lat: parseFloat(m[1]), lon: parseFloat(m[3]) };

    // @lat,lon (google)
    m = s.match(/@(-?\d+(\.\d+)?),(-?\d+(\.\d+)?)/);
    if (m) return { lat: parseFloat(m[1]), lon: parseFloat(m[3]) };

    // q=lat,lon
    m = s.match(/[?&]q=(-?\d+(\.\d+)?),(-?\d+(\.\d+)?)/i);
    if (m) return { lat: parseFloat(m[1]), lon: parseFloat(m[3]) };

    // plain "lat,lon"
    m = s.match(/(-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)/);
    if (m) return { lat: parseFloat(m[1]), lon: parseFloat(m[3]) };

    // nothing matched
    return null;
  } catch {
    return null;
  }
};

/* -------------------- Styled helpers -------------------- */

const ScrollToTop = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  zIndex: 1000,
  "&:hover": { backgroundColor: theme.palette.primary.dark },
  [theme.breakpoints.down("sm")]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  textTransform: "none",
  marginBottom: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[1],
  "&:hover": { boxShadow: theme.shadows[3] },
}));

const HeaderCard = styled(DetailsCard)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(
    theme.palette.background.paper,
    0.95
  )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
  backdropFilter: "blur(10px)",
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(4),
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(
      theme.palette.primary.light,
      0.7
    )})`,
  },
}));

const ImageGallery = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "3fr 2fr",
  gap: theme.spacing(2),
  width: "100%",

  ".main-image": {
    gridColumn: "1 / 2",
    gridRow: "1 / -1",
    height: "100%",
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    boxShadow: theme.shadows[2],
    cursor: "pointer",
    transition: "transform 0.3s ease",
    "&:hover": { transform: "scale(1.02)" },
    "& img": { width: "100%", height: "100%", objectFit: "cover" },
  },

  ".secondary-images": {
    gridColumn: "2 / 3",
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gridAutoRows: "1fr",
    gap: theme.spacing(2),
    height: "100%",
    "& .image-container": {
      position: "relative",
      aspectRatio: "1/1",
      borderRadius: theme.shape.borderRadius,
      overflow: "hidden",
      boxShadow: theme.shadows[1],
      cursor: "pointer",
      transition: "transform 0.3s ease",
      "&:hover": { transform: "scale(1.02)" },
      "& img": { width: "100%", height: "100%", objectFit: "cover" },
      "& .image-label": {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: theme.spacing(1),
        background: "rgba(0,0,0,0.7)",
        color: "white",
        fontSize: "0.75rem",
        textAlign: "center",
        transform: "translateY(100%)",
        transition: "transform 0.3s ease",
      },
      "&:hover .image-label": { transform: "translateY(0)" },
    },
  },

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    ".main-image": { gridColumn: "1", gridRow: "auto", aspectRatio: "16/9" },
    ".secondary-images": {
      gridColumn: "1",
      gridRow: "auto",
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
}));

/* Floating back button styles — paste this with your other styled helpers (above the component) */
const FloatingBack = styled("div")(({ theme }) => ({
  position: "fixed",
  right: theme.spacing(4),
  top: theme.spacing(6),
  zIndex: 1400,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  pointerEvents: "auto",
  [theme.breakpoints.down("sm")]: {
    right: theme.spacing(2),
    top: "auto",
    bottom: theme.spacing(3),
  },
}));

const FloatingFab = styled(Fab)(({ theme }) => ({
  width: 56,
  height: 56,
  minHeight: 56,
  borderRadius: 16,
  boxShadow: theme.shadows[6],
  backdropFilter: "blur(6px)",
  transition: "transform .18s ease, box-shadow .18s ease",
  "&:hover": { transform: "translateY(-3px)" },
  "&:focus": {
    outline: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  },
}));

const FloatingLabel = styled("div")(({ theme }) => ({
  background: theme.palette.mode === "dark" ? "rgba(0,0,0,0.7)" : "#fff",
  color: theme.palette.text.primary,
  padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  display: "flex",
  flexDirection: "column",
  gap: 6,
  minWidth: 180,
  transformOrigin: "right center",
  transition: "opacity .18s ease, transform .18s ease",
  willChange: "transform, opacity",
}));

const PulseDot = styled("span")(({ theme }) => ({
  width: 8,
  height: 8,
  borderRadius: "999px",
  background: theme.palette.primary.main,
  boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.35)}`,
  animation: "pulse 2s infinite",
  "@keyframes pulse": {
    "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.35)}` },
    "70%": { boxShadow: `0 0 0 12px rgba(0,0,0,0)` },
    "100%": { boxShadow: `0 0 0 0 rgba(0,0,0,0)` },
  },
}));

/* ---------- Fab close for floating button ---------- */

/* -------------------- Component -------------------- */

const ProjectDetails = ({ open, onClose, projectId }) => {
  const { id: idFromRoute } = useParams();
  const id = projectId || idFromRoute;
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [modalImage, setModalImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [periodFilter, setPeriodFilter] = useState("all");
  const [filterMethod, setFilterMethod] = useState("period");
  const [showScrollTop, setShowScrollTop] = useState(false);

  const { isSessionExpired, handleSessionExpired, closeSessionExpired } =
    useSessionExpired();
  const theme = useTheme();
  // small-screen detector
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // floating FAB state & ref for label expansion
  const [fabExpanded, setFabExpanded] = useState(false);
  const fabRef = useRef(null);
  // FAB hover handling — place inside ProjectDetails component (with other hooks)
  const fabCloseTimeoutRef = useRef(null);
  const FAB_CLOSE_DELAY = 140; // ms, tweak if needed

  const handleParentEnter = () => {
    if (fabCloseTimeoutRef.current) {
      clearTimeout(fabCloseTimeoutRef.current);
      fabCloseTimeoutRef.current = null;
    }
    setFabExpanded(true);
  };

  const handleParentLeave = () => {
    if (fabCloseTimeoutRef.current) clearTimeout(fabCloseTimeoutRef.current);
    fabCloseTimeoutRef.current = setTimeout(() => {
      setFabExpanded(false);
      fabCloseTimeoutRef.current = null;
    }, FAB_CLOSE_DELAY);
  };

  // cleanup when component unmounts
  useEffect(() => {
    return () => {
      if (fabCloseTimeoutRef.current) {
        clearTimeout(fabCloseTimeoutRef.current);
        fabCloseTimeoutRef.current = null;
      }
    };
  }, []);

  const mapContainerRef = useRef(null);
  const [mapInteractive, setMapInteractive] = useState(false);

  /* ---------- Token helper ---------- */
  const getValidToken = useCallback(() => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token || token.split(".").length !== 3) {
      handleSessionExpired();
      return null;
    }
    return token;
  }, [handleSessionExpired]);

  /* ---------- Normalizer ---------- */
  const normalizeProject = useCallback((raw) => {
    const data = raw?.data ?? raw;
    const fin = raw?.financial_data ?? {};

    const parseNumberSafe = (v) => {
      if (v === null || v === undefined || v === "") return 0;
      const n = Number(String(v).replace(/,/g, ""));
      return Number.isFinite(n) ? n : 0;
    };

    const formatDisplayDate = (dateStr) => {
      if (!dateStr) return "N/A";
      const d = parseApiDate(dateStr);
      if (!d || isNaN(d)) return dateStr;
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
      });
    };

    const base = {
      id: data?.project_id || data?.id || "N/A",
      key: data?.key ?? data?.project_key ?? null,
      title:
        data?.title && String(data.title).trim() !== ""
          ? String(data.title)
          : data?.key != null && String(data.key).trim() !== ""
          ? String(data.key)
          : data?.name || "Untitled Project",
      status: data?.status || data?.stage || "N/A",
      stage: data?.stage ?? data?.status ?? "N/A",
      city: data?.location || data?.city || data?.compound?.name || "N/A",
      developer:
        data?.developer?.name ||
        (data?.developer ? String(data.developer) : "N/A"),
      project: data?.project || data?.compound?.name || "N/A",
      type: data?.type || "N/A",
      unitNo: data?.unit_no || data?.unit || data?.unitNo || "N/A",
      area: data?.area ?? "N/A",
      gardenArea: data?.garden_area ?? null,
      bedrooms: data?.bedrooms ?? data?.bdr ?? "N/A",
      bathrooms: data?.bathrooms ?? "N/A",
      floor: data?.floor ?? "N/A",
      target1: data?.target_1 || data?.investment_type || "N/A",
      target2: data?.target_2 || null,
      entryDateRaw:
        data?.entry_date || data?.reservation_date || data?.created_at || null,
      exitDateRaw: data?.exit_date || data?.contract_date || null,
      entryDate: formatDisplayDate(
        data?.entry_date || data?.reservation_date || data?.created_at
      ),
      exitDate: formatDisplayDate(data?.exit_date || data?.contract_date),
      // prefer financial_data values (top-level) then fall back to older fields
      investedAmount: parseNumberSafe(
        fin?.investment_amount ??
          data?.invested_amount ??
          data?.total_contract_value ??
          0
      ),
      totalProfit: parseNumberSafe(
        fin?.total_profit ??
          data?.investor_total_profit ??
          data?.total_profit ??
          0
      ),
      operationProfit: parseNumberSafe(
        fin?.operation_profit ?? data?.operation_profit ?? 0
      ),
      assetProfit: parseNumberSafe(
        fin?.asset_profit ?? data?.asset_profit ?? data?.asset_evaluation ?? 0
      ),
      currency: fin?.currency ?? data?.currency ?? "USD",
      monthlyData: data?.graph_data
        ? {
            labels: data.graph_data.months || [],
            totalProfit: data.graph_data.total_profit || [],
            operationProfit: data.graph_data.operation_profit || [],
            assetProfit: data.graph_data.asset_profit || [],
          }
        : null,
      // keep original map_location as provided by API (if present)
      mapLocation:
        data?.map_location ||
        (data?.latitude && data?.longitude
          ? `https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`
          : null),
      documents: data?.documents ?? data?.document ?? null,
    };

    const imagesArray = Array.isArray(data?.images)
      ? data.images.filter(Boolean)
      : [];

    const photosArray = imagesArray
      .map((img) => ({
        url:
          img.preview_url ||
          img.url ||
          img.thumbnail_url ||
          img.file_name ||
          null,
        label: img.name || img.file_name || "",
      }))
      .filter((p) => p.url);

    const legacyImages = {
      unit3d: photosArray[0]?.url || null,
      project1: photosArray[1]?.url || null,
      project2: photosArray[2]?.url || null,
      unitLocation: photosArray[3]?.url || null,
      document: data?.document || data?.documents || null,
    };

    const pdfFromDocs =
      typeof data?.documents === "string"
        ? data.documents
            .split(",")
            .map((s) => s.trim())
            .find((s) => s.toLowerCase().endsWith(".pdf"))
        : null;

    return {
      ...base,
      photos: photosArray,
      images: legacyImages,
      pdfUrl:
        data?.document_url ||
        data?.pdf_url ||
        data?.document ||
        pdfFromDocs ||
        null,
      _raw: data,
    };
  }, []);

  const location = useLocation();

  /* Always scroll to top when opening this detail page.
   - runs on mount and when `id` changes
   - also runs when sidebar navigates to same URL but with a different state (sidebarClickId)
*/
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && fabExpanded) setFabExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fabExpanded]);

  // allow Escape to disable map interaction
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && mapInteractive) {
        setMapInteractive(false);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [mapInteractive]);
  /* ---------- Fetch ---------- */
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });

    const fetchProjectDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = getValidToken();
        if (!token) return;

        const resp = await axios.get(
          `${process.env.REACT_APP_API_URL}/projects/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const payload = resp?.data;

        if (payload?.error === "No financial data found for this project") {
          const normalized = normalizeProject(payload);
          setProject({
            ...normalized,
            investedAmount: 0,
            totalProfit: 0,
            operationProfit: 0,
            assetProfit: 0,
            monthlyData: null,
          });

          // persist minimal info for sidebar fallback
          try {
            sessionStorage.setItem(
              "lastOpenedProject",
              JSON.stringify({ id: normalized.id, title: normalized.title })
            );
          } catch (e) {
            /* ignore */
          }

          return;
        }

        const normalized = normalizeProject(payload);
        setProject(normalized);

        // Persist minimal info for sidebar fallback / refresh
        try {
          sessionStorage.setItem(
            "lastOpenedProject",
            JSON.stringify({ id: normalized.id, title: normalized.title })
          );
        } catch (e) {
          // ignore storage errors
        }
      } catch (err) {
        console.error("Error details:", err);
        if (
          err.response?.data?.message?.includes("token") ||
          err.response?.status === 401 ||
          err.message?.includes("token")
        ) {
          handleSessionExpired();
          return;
        }
        setError("An error occurred while fetching project details");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchProjectDetails();
  }, [id, getValidToken, handleSessionExpired, normalizeProject]);

  /* ---------- Modal interactions ---------- */
  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY < 0) setZoomLevel((prev) => Math.min(prev + 0.1, 3));
    else setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  };

  useEffect(() => {
    document.body.style.overflow = modalImage ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [modalImage]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleImageError = (event) => {
    event.target.src = "/placeholder-image.jpg";
  };

  /* ---------- Chart (Bar) ---------- */
  const getFilteredChartData = () => {
    if (!project?.monthlyData) return null;
    let filteredData = project.monthlyData;
    if (filterMethod === "period") {
      if (periodFilter === "3m")
        filteredData = filterLastNMonths(project.monthlyData, 3);
      if (periodFilter === "6m")
        filteredData = filterLastNMonths(project.monthlyData, 6);
      if (periodFilter === "1y")
        filteredData = filterLastNMonths(project.monthlyData, 12);
    } else {
      filteredData = filterByDateRange(
        project.monthlyData,
        dateRange.start,
        dateRange.end
      );
    }

    return {
      labels: filteredData.labels.map(formatMonthYear),
      datasets: [
        {
          label: "Total Profit",
          data: filteredData.totalProfit,
          backgroundColor: "rgba(76, 175, 80, 0.6)",
        },
        {
          label: "Operation Profit",
          data: filteredData.operationProfit,
          backgroundColor: "rgba(33, 150, 243, 0.6)",
        },
        {
          label: "Asset Profit",
          data: filteredData.assetProfit,
          backgroundColor: "rgba(255, 193, 7, 0.6)",
        },
      ],
    };
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: { display: true, text: "Profit Breakdown (by month)" },
      tooltip: { mode: "index", intersect: false },
    },
    scales: {
      x: { stacked: false, grid: { display: false } },
      y: {
        stacked: false,
        beginAtZero: true,
        ticks: { callback: (value) => value.toLocaleString() },
      },
    },
  };

  /* ---------- Scroll-to-top ---------- */
  useEffect(() => {
    // Use instant jump to top to avoid visual jump when mounting;
    // if you prefer smooth animation change behavior to 'smooth'.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // also ensure document element scrolled (safer for some browsers/layouts)
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [id, location?.state?.sidebarClickId]);

  const getAvailableDates = (data) => {
    if (!data || !data.labels || data.labels.length === 0)
      return { min: null, max: null, availableDates: [] };
    const dates = data.labels.map(parseApiDate);
    return {
      min: dates[0],
      max: dates[dates.length - 1],
      availableDates: dates,
    };
  };

  /* ---------- Render ---------- */
  if (isLoading) return <ProjectDetailsSkeleton />;

  if (error) {
    return (
      <Box sx={{ display: "flex" }}>
        <AnimatedBackground />
        <MuiSidebar />
        <Box sx={{ flexGrow: 1, p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </Box>
    );
  }

  if (!project) return null;

  // --- Map coordinates resolution (use cached mapLocation string from normalizer)
  const rawMap =
    project.mapLocation ||
    project.map_location ||
    project._raw?.map_location ||
    null;
  const coords = extractCoordinatesFromGoogleMaps(rawMap) ||
    (project.latitude && project.longitude
      ? { lat: Number(project.latitude), lon: Number(project.longitude) }
      : null) || { lat: 30.0444, lon: 31.2357 }; // Cairo fallback

  const lat = Number(coords.lat);
  const lon = Number(coords.lon);
  const delta = 0.01; // small bbox delta
  const bbox = `${lon - delta},${lat - delta},${lon + delta},${lat + delta}`;
  const marker = `${lat}%2C${lon}`; // lat,lon percent-encoded comma

  const osmEmbedSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`;

  // Build Google Maps link — prefer existing google/maps link if it already points to maps
  let googleMapsHref = null;
  if (rawMap && /google|maps\.google|maps\.app/i.test(String(rawMap))) {
    googleMapsHref = rawMap;
  } else {
    googleMapsHref = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
  }

  return (
    <Box sx={{ display: "flex" }}>
      <AnimatedBackground />
      <MuiSidebar />

      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>
        <DetailsContainer>
          {/* Floating back action (creative & accessible) */}
          <FloatingBack aria-hidden={false}>
            <div
              // parent hover region — covers both FAB and label so moving between them won't fire leave
              onMouseEnter={() => !isSmall && handleParentEnter()}
              onMouseLeave={() => !isSmall && handleParentLeave()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {/* label panel (kept for aria/live) */}
              <div
                role="region"
                aria-live="polite"
                style={{
                  display: fabExpanded ? "block" : isSmall ? "none" : "none",
                  marginRight: 8,
                }}
              />

              <div style={{ display: "flex", alignItems: "center" }}>
                <FloatingFab
                  color="primary"
                  aria-label="Back to projects"
                  ref={fabRef}
                  onFocus={() => setFabExpanded(true)}
                  onBlur={() => setFabExpanded(false)}
                  onClick={() => {
                    // if on small device, first expand to show label (tap-to-show), second tap goes back
                    if (isSmall && !fabExpanded) {
                      setFabExpanded(true);
                      return;
                    }
                    navigate("/projects");
                    if (typeof onClose === "function") onClose();
                  }}
                >
                  <ArrowBackIcon />
                </FloatingFab>

                {/* Expanding label / mini card */}
                <div
                  style={{
                    marginLeft: 10,
                    display: fabExpanded ? "flex" : "none",
                    alignItems: "center",
                  }}
                >
                  <FloatingLabel
                    role="dialog"
                    aria-label="Back action"
                    // make sure label accepts pointer events
                    style={{ pointerEvents: "auto" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          Back to Projects
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {project?.title ? project.title : "Project details"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PulseDot />
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          navigate("/projects");
                          if (typeof onClose === "function") onClose();
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Go back
                      </Button>
                      {/* <Button
                        size="small"
                        variant="outlined"
                        onClick={() =>
                          window.open("/projects", "_blank", "noopener")
                        }
                        sx={{ textTransform: "none" }}
                      >
                        Open in new tab
                      </Button> */}
                    </Box>
                  </FloatingLabel>
                </div>
              </div>
            </div>
          </FloatingBack>

          {/* === Updated HeaderCard: cleaner, no ID shown, shows title + key + small meta row === */}
          <HeaderCard>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={project.title}
                  >
                    {project.title}
                  </Typography>

                  {/* subtle secondary row: project key (if present) */}
                  {project.key && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.5,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontStyle: "normal",
                      }}
                      title={project.key}
                    >
                      <span style={{ fontWeight: 600, marginRight: 6 }}>
                        Key:
                      </span>
                      {project.key}
                    </Typography>
                  )}
                </Box>

                <Chip
                  label={project.status}
                  color={
                    String(project.status).toLowerCase().includes("on")
                      ? "success"
                      : "info"
                  }
                  sx={{ height: 28, ml: 1, boxShadow: 1 }}
                />
              </Box>

              {/* small meta row: developer + city (with icons) */}
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LocationCityIcon
                    fontSize="small"
                    sx={{ color: "action.active" }}
                  />
                  {project.city || "N/A"}
                </Typography>

                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <BusinessIcon
                    fontSize="small"
                    sx={{ color: "action.active" }}
                  />
                  {project.developer || "N/A"}
                </Typography>

                {/* optional small 'project type' or 'unit' badge if you want */}
                {project.unitNo && (
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.secondary",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <TagIcon fontSize="small" sx={{ color: "action.active" }} />
                    {project.unitNo}
                  </Typography>
                )}
              </Box>
            </Box>
          </HeaderCard>

          {/* Photos */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Photos
            </Typography>
            {project.photos && project.photos.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 2,
                }}
              >
                {project.photos.map((p, idx) => (
                  <Box
                    key={`${p.url}-${idx}`}
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: 1,
                      cursor: "pointer",
                      aspectRatio: "4 / 3",
                    }}
                    onClick={() =>
                      setModalImage({
                        url: p.url,
                        label: p.label || `Photo ${idx + 1}`,
                      })
                    }
                  >
                    <img
                      src={p.url}
                      alt={p.label || `Photo ${idx + 1}`}
                      onError={(e) => (e.target.src = "/placeholder-image.jpg")}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    {p.label && (
                      <Typography
                        sx={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          bottom: 0,
                          p: 1,
                          fontSize: "0.8rem",
                          color: "#fff",
                          bgcolor: "rgba(0,0,0,0.55)",
                          textAlign: "center",
                        }}
                      >
                        {p.label}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                Photos will appear here once provided by the API.
              </Box>
            )}
          </DetailsCard>

          {/* Legacy Images */}
          {/* <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Project Images
            </Typography>
            <ImageGallery>
              {project.images.unit3d && (
                <Box
                  className="main-image"
                  onClick={() =>
                    setModalImage({
                      url: project.images.unit3d,
                      label: "Unit 3D View",
                    })
                  }
                >
                  <img
                    src={project.images.unit3d}
                    alt="Unit 3D View"
                    onError={handleImageError}
                    loading="lazy"
                  />
                </Box>
              )}
              <Box className="secondary-images">
                {Object.entries(project.images)
                  .filter(([key, val]) => key !== "unit3d" && !!val)
                  .map(([key, url]) => (
                    <Box
                      key={key}
                      className="image-container"
                      onClick={() =>
                        setModalImage({
                          url,
                          label: key
                            .replace(/_/g, " ")
                            .replace(/([A-Z])/g, " $1")
                            .trim(),
                        })
                      }
                    >
                      <img
                        src={url}
                        alt={key.replace(/_/g, " ")}
                        onError={handleImageError}
                        loading="lazy"
                      />
                      <Typography className="image-label">
                        {key
                          .replace(/_/g, " ")
                          .replace(/([A-Z])/g, " $1")
                          .trim()}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </ImageGallery>
          </DetailsCard> */}

          {/* Property Information */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Property Information
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                gap: 3,
              }}
            >
              <InfoTile
                icon={<LocationCityIcon color="primary" />}
                label="City"
                value={project.city}
              />
              <InfoTile
                icon={<BusinessIcon color="primary" />}
                label="Developer"
                value={project.developer}
              />
              <InfoTile
                icon={<HomeWorkIcon color="primary" />}
                label="Project"
                value={project.project}
              />
              <InfoTile
                icon={<HouseIcon color="primary" />}
                label="Type"
                value={project.type}
              />
              <InfoTile
                icon={<TagIcon color="primary" />}
                label="Unit No"
                value={project.unitNo}
              />
              <InfoTile
                icon={<SquareFootIcon color="primary" />}
                label="Area"
                value={`${project.area}${
                  project.area && project.area !== "N/A" ? "㎡" : ""
                }`}
              />
              {project.gardenArea && (
                <InfoTile
                  icon={<GrassIcon color="primary" />}
                  label="Garden Area"
                  value={`${project.gardenArea}㎡`}
                />
              )}
              <InfoTile
                icon={<KingBedIcon color="primary" />}
                label="Bedrooms"
                value={project.bdr}
              />
              <InfoTile
                icon={<StairsIcon color="primary" />}
                label="Floor"
                value={project.floor}
              />
            </Box>
          </DetailsCard>

          {/* Financial Overview */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Financial Overview
            </Typography>
            <DetailsGrid>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Investment Amount
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    project.investedAmount > 0
                      ? "text.primary"
                      : "text.secondary"
                  }
                >
                  {project.investedAmount > 0
                    ? Number(project.investedAmount).toLocaleString()
                    : "No Investment"}
                </Typography>
              </Box>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Total Profit
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    project.totalProfit > 0 ? "success.main" : "text.secondary"
                  }
                >
                  {project.totalProfit > 0
                    ? Number(project.totalProfit).toLocaleString()
                    : "No Profit"}
                </Typography>
              </Box>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Operation Profit
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    project.operationProfit > 0 ? "info.main" : "text.secondary"
                  }
                >
                  {project.operationProfit > 0
                    ? project.operationProfit.toLocaleString()
                    : "No Operation Profit"}
                </Typography>
              </Box>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Asset Profit
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    project.assetProfit > 0 ? "warning.main" : "text.secondary"
                  }
                >
                  {project.assetProfit > 0
                    ? project.assetProfit.toLocaleString()
                    : "No Asset Profit"}
                </Typography>
              </Box>
            </DetailsGrid>
          </DetailsCard>

          {/* Investment Timeline */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Investment Timeline
            </Typography>
            <DetailsGrid>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Entry Date
                </Typography>
                <Typography variant="body1">{project.entryDate}</Typography>
              </Box>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Exit Date
                </Typography>
                <Typography variant="body1">{project.exitDate}</Typography>
              </Box>
              <Box className="detail-item">
                <Typography variant="body2" color="text.secondary">
                  Investment Target
                </Typography>
                <Typography variant="body1">{project.target1}</Typography>
              </Box>
              {project.target2 !== "None" && (
                <Box className="detail-item">
                  <Typography variant="body2" color="text.secondary">
                    Secondary Target
                  </Typography>
                  <Typography variant="body1">{project.target2}</Typography>
                </Box>
              )}
            </DetailsGrid>
          </DetailsCard>

          {/* Profit Chart (Bar) */}
          {project.monthlyData && (
            <DetailsCard>
              <Typography variant="h6" gutterBottom>
                Profit Overview
              </Typography>
              <Box sx={{ mb: 3 }}>
                <Box
                  display="flex"
                  gap={2}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mb: 2 }}
                >
                  <Select
                    size="small"
                    value={filterMethod}
                    onChange={(e) => setFilterMethod(e.target.value)}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value="period">Period</MenuItem>
                    <MenuItem value="range">Date Range</MenuItem>
                  </Select>

                  {filterMethod === "period" ? (
                    <Select
                      size="small"
                      value={periodFilter}
                      onChange={(e) => setPeriodFilter(e.target.value)}
                      sx={{ minWidth: 160 }}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="1y">Last Year</MenuItem>
                      <MenuItem value="6m">Last 6 Months</MenuItem>
                      <MenuItem value="3m">Last 3 Months</MenuItem>
                    </Select>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <Box display="flex" gap={2}>
                        <DatePicker
                          label="Start Date"
                          views={["year", "month"]}
                          value={
                            dateRange.start
                              ? parseApiDate(dateRange.start)
                              : null
                          }
                          minDate={getAvailableDates(project.monthlyData).min}
                          maxDate={getAvailableDates(project.monthlyData).max}
                          shouldDisableMonth={(date) => {
                            const available = getAvailableDates(
                              project.monthlyData
                            ).availableDates;
                            return !available.some(
                              (d) =>
                                d.getMonth() === date.getMonth() &&
                                d.getFullYear() === date.getFullYear()
                            );
                          }}
                          onChange={(date) => {
                            if (!date) return;
                            const month = date.getMonth() + 1;
                            const year = date.getFullYear();
                            setDateRange((prev) => ({
                              ...prev,
                              start: `${month}/1/${year}`,
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} size="small" />
                          )}
                        />
                        <DatePicker
                          label="End Date"
                          views={["year", "month"]}
                          value={
                            dateRange.end ? parseApiDate(dateRange.end) : null
                          }
                          minDate={
                            dateRange.start
                              ? parseApiDate(dateRange.start)
                              : getAvailableDates(project.monthlyData).min
                          }
                          maxDate={getAvailableDates(project.monthlyData).max}
                          shouldDisableMonth={(date) => {
                            const available = getAvailableDates(
                              project.monthlyData
                            ).availableDates;
                            return !available.some(
                              (d) =>
                                d.getMonth() === date.getMonth() &&
                                d.getFullYear() === date.getFullYear()
                            );
                          }}
                          onChange={(date) => {
                            if (!date) return;
                            const month = date.getMonth() + 1;
                            const year = date.getFullYear();
                            setDateRange((prev) => ({
                              ...prev,
                              end: `${month}/1/${year}`,
                            }));
                          }}
                          renderInput={(params) => (
                            <TextField {...params} size="small" />
                          )}
                        />
                      </Box>
                    </LocalizationProvider>
                  )}
                </Box>
              </Box>

              <Box
                sx={{ height: 400, display: "flex", justifyContent: "center" }}
              >
                <Bar data={getFilteredChartData()} options={barOptions} />
              </Box>
            </DetailsCard>
          )}

          {/* Map Location */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Map Location
            </Typography>

            {rawMap || project.latitude || project.longitude ? (
              <>
                <Box
                  ref={mapContainerRef}
                  sx={{
                    position: "relative",
                    height: { xs: 300, sm: 400 },
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    mb: 2,
                    // make sure the map container doesn't trap scroll when not interactive
                    touchAction: mapInteractive ? "auto" : "pan-y",
                  }}
                >
                  {/* iframe: disable pointer events by default so page scroll continues */}
                  <iframe
                    src={osmEmbedSrc}
                    loading="lazy"
                    title="Project Location"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      pointerEvents: mapInteractive ? "auto" : "none",
                    }}
                  />

                  {/* Overlay shown when map is NOT interactive */}
                  {!mapInteractive && (
                    <Box
                      onClick={() => setMapInteractive(true)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          setMapInteractive(true);
                      }}
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.45))",
                        color: "#fff",
                        cursor: "pointer",
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Click to interact with the map
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Use mouse wheel, drag or pinch to zoom. Tap to
                          interact on mobile.
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{ mt: 2, textTransform: "none" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMapInteractive(true);
                          }}
                        >
                          Click to interact
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Small control to disable interaction when map is interactive */}
                  {mapInteractive && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        zIndex: 10,
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setMapInteractive(false)}
                        sx={{
                          textTransform: "none",
                          bgcolor: "rgba(255,255,255,0.9)",
                        }}
                      >
                        Disable interaction
                      </Button>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    href={googleMapsHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<FiMap />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      boxShadow: 2,
                    }}
                  >
                    View on Google Maps
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                Map location will appear here once provided by the API.
              </Box>
            )}
          </DetailsCard>

          {/* Project Document */}
          <DetailsCard>
            <Typography variant="h6" gutterBottom>
              Project Document
            </Typography>
            {project.pdfUrl ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 2,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <FiFile style={{ fontSize: 24 }} />
                <Typography
                  sx={{
                    ml: 1.5,
                    flexGrow: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={project.pdfUrl}
                >
                  {project.pdfUrl.split("/").pop()}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FiDownload />}
                  onClick={() => window.open(project.pdfUrl, "_blank")}
                  sx={{ ml: 2, textTransform: "none", borderRadius: 2 }}
                >
                  View / Download
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  color: "text.secondary",
                }}
              >
                The project PDF will appear here once provided by the API.
              </Box>
            )}
          </DetailsCard>

          {/* Image Modal */}
          {modalImage && (
            <div
              className="image-modal"
              onClick={() => {
                setModalImage(null);
                setZoomLevel(1);
                setPosition({ x: 0, y: 0 });
              }}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
              }}
            >
              <div
                className="image-modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "relative",
                  background: "#111",
                  borderRadius: 8,
                  padding: 12,
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  width: "min(1100px, 90vw)",
                }}
              >
                <div
                  className="modal-image-container"
                  onWheel={handleWheel}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  style={{
                    overflow: "hidden",
                    width: "100%",
                    height: "70vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: zoomLevel > 1 ? "grab" : "zoom-in",
                  }}
                >
                  <img
                    src={modalImage.url}
                    alt={modalImage.label}
                    style={{
                      transform: `scale(${zoomLevel}) translate(${position.x}px, ${position.y}px)`,
                      transition: isDragging
                        ? "none"
                        : "transform 0.2s ease-out",
                      userSelect: "none",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      pointerEvents: "none",
                    }}
                    draggable="false"
                  />
                </div>

                <span
                  className="image-modal-label"
                  style={{ color: "#fff", fontSize: 14 }}
                >
                  {modalImage.label}
                </span>

                <button
                  className="close-modal"
                  onClick={() => {
                    setModalImage(null);
                    setZoomLevel(1);
                    setPosition({ x: 0, y: 0 });
                  }}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "transparent",
                    border: "none",
                    fontSize: 28,
                    color: "#fff",
                    cursor: "pointer",
                    lineHeight: 1,
                  }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
          )}

          {/* Scroll to Top */}
          <ScrollToTop
            color="primary"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            sx={{ display: showScrollTop ? "flex" : "none" }}
          >
            <KeyboardArrowUpIcon />
          </ScrollToTop>

          <SessionExpiredPopup
            isVisible={isSessionExpired}
            open={isSessionExpired}
            onClose={closeSessionExpired}
            onRelogin={() => {
              try {
                localStorage.removeItem("token");
                sessionStorage.removeItem("token");
                localStorage.removeItem("user");
                sessionStorage.removeItem("user");
              } catch (e) {
                /* ignore */
              }
              window.location.replace("/");
            }}
          />
        </DetailsContainer>
      </Box>
    </Box>
  );
};

/* Small helper for info tiles */
const InfoTile = ({ icon, label, value }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      borderRadius: 1,
      bgcolor: "background.paper",
      boxShadow: 1,
      transition: "all 0.3s ease",
      "&:hover": { boxShadow: 3, transform: "translateY(-2px)" },
    }}
  >
    {icon}
    <Box>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight="500">
        {value ?? "N/A"}
      </Typography>
    </Box>
  </Box>
);

export default ProjectDetails;
